// Offline Office-doc previewer.
//   • xlsx / xls / csv  → SheetJS parses into a workbook → render as HTML tables (one per sheet)
//   • docx              → mammoth.js converts to styled HTML
//   • doc (legacy)      → no inline render; download fallback
//
// We never send the file anywhere — it's parsed entirely in the browser
// from the same authenticated Blob URL the rest of the preview pane uses.

import { useEffect, useMemo, useState } from "react";
import { AlertCircle, Download, Loader2 } from "lucide-react";

const EXCEL_EXT = /\.(xlsx|xls|csv)$/i;
const WORD_EXT  = /\.(docx)$/i;

export function isOfficePreviewable({ mime, name }) {
  const n = name || "";
  if (EXCEL_EXT.test(n) || WORD_EXT.test(n)) return true;
  if (!mime) return false;
  return (
    mime.includes("spreadsheetml") ||
    mime === "application/vnd.ms-excel" ||
    mime === "text/csv" ||
    mime === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  );
}

function kindFor({ mime, name }) {
  const n = name || "";
  if (EXCEL_EXT.test(n)) return "excel";
  if (WORD_EXT.test(n))  return "word";
  if (mime?.includes("spreadsheetml") || mime === "application/vnd.ms-excel" || mime === "text/csv") return "excel";
  if (mime?.includes("officedocument.wordprocessingml.document")) return "word";
  return null;
}

export default function OfficePreview({ url, mime, name }) {
  const kind = useMemo(() => kindFor({ mime, name }), [mime, name]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [content, setContent] = useState(null); // {type, payload}

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      setContent(null);
      try {
        const buf = await (await fetch(url)).arrayBuffer();
        if (cancelled) return;

        if (kind === "excel") {
          const XLSX = await import("xlsx");
          const wb = XLSX.read(buf, { type: "array" });
          const sheets = wb.SheetNames.map((sname) => ({
            name: sname,
            html: XLSX.utils.sheet_to_html(wb.Sheets[sname], { editable: false }),
          }));
          if (!cancelled) setContent({ type: "excel", sheets });
        } else if (kind === "word") {
          const mammoth = await import("mammoth/mammoth.browser");
          const result = await mammoth.convertToHtml({ arrayBuffer: buf });
          if (!cancelled) setContent({ type: "word", html: result.value });
        } else {
          throw new Error("Unsupported file type");
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to render preview");
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [url, kind]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-[#6B7280]">
        <Loader2 className="h-7 w-7 animate-spin mb-2" />
        <p className="text-sm font-body">Rendering preview…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
        <AlertCircle className="h-7 w-7 text-amber-500 mb-2" />
        <p className="text-sm font-heading font-semibold text-[#1A1A2E]">
          Couldn't render this file in-window
        </p>
        <p className="text-xs text-[#6B7280] font-body max-w-md mt-1 mb-4">
          {error}
        </p>
        <a
          href={url}
          download={name}
          className="inline-flex items-center gap-1.5 text-sm font-body font-semibold text-[#6C63FF] border border-[#6C63FF]/30 hover:bg-[#EEEDFC] rounded-lg px-3 py-2"
        >
          <Download className="h-4 w-4" /> Download to view
        </a>
      </div>
    );
  }

  if (content?.type === "excel") {
    return (
      <div className="excel-preview p-3 bg-white">
        {content.sheets.length > 1 && (
          <p className="text-[10px] font-heading font-bold uppercase tracking-wider text-[#9CA3AF] mb-2">
            {content.sheets.length} sheet{content.sheets.length === 1 ? "" : "s"}
          </p>
        )}
        {content.sheets.map((s) => (
          <div key={s.name} className="mb-6">
            {content.sheets.length > 1 && (
              <h4 className="text-xs font-heading font-bold text-[#1A1A2E] mb-2 bg-[#EEEDFC] inline-block px-2 py-0.5 rounded">
                {s.name}
              </h4>
            )}
            <div
              className="overflow-x-auto text-xs font-body excel-sheet"
              // SheetJS emits a safe HTML <table>; we render it inline.
              dangerouslySetInnerHTML={{ __html: s.html }}
            />
          </div>
        ))}
        <style>{`
          .excel-sheet table { border-collapse: collapse; }
          .excel-sheet table, .excel-sheet th, .excel-sheet td {
            border: 1px solid #E8E6F8;
          }
          .excel-sheet th, .excel-sheet td {
            padding: 4px 8px;
            white-space: nowrap;
          }
          .excel-sheet th {
            background: #FAFAFE;
            font-weight: 600;
            color: #1A1A2E;
          }
        `}</style>
      </div>
    );
  }

  if (content?.type === "word") {
    return (
      <div className="word-preview p-6 bg-white max-w-3xl mx-auto">
        <div
          className="prose prose-sm font-body text-[#1A1A2E]"
          dangerouslySetInnerHTML={{ __html: content.html }}
        />
        <style>{`
          .word-preview p { margin: 0 0 10px 0; line-height: 1.55; }
          .word-preview h1, .word-preview h2, .word-preview h3 {
            font-family: var(--font-heading, inherit);
            color: #1A1A2E;
            margin: 18px 0 8px 0;
          }
          .word-preview ul, .word-preview ol { padding-left: 20px; margin-bottom: 10px; }
          .word-preview table { border-collapse: collapse; margin: 8px 0; }
          .word-preview table, .word-preview th, .word-preview td { border: 1px solid #E8E6F8; }
          .word-preview th, .word-preview td { padding: 4px 8px; }
        `}</style>
      </div>
    );
  }

  return null;
}
